import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import apiClient from '../utils/apiClient';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  const login = useCallback(async (credentials) => {
    try {
      const response = await apiClient.post('/auth/login', credentials);
      const { user: userData, token: authToken } = response.data;

      setUser(userData);
      setToken(authToken);
      localStorage.setItem('token', authToken);
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;

      message.success(response.data.message || 'Login successful');
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Login failed';
      message.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    delete apiClient.defaults.headers.common['Authorization'];
    message.success('Logged out successfully');
  }, []);

  const getCurrentUser = useCallback(async () => {
    try {
      const response = await apiClient.get('/auth/me');
      setUser(response.data.user);
    } catch (error) {
      console.error('Get current user error:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [logout]);

  const refreshToken = useCallback(async () => {
    try {
      const response = await apiClient.post('/auth/refresh');
      const { token: newToken } = response.data;

      setToken(newToken);
      localStorage.setItem('token', newToken);
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

      return newToken;
    } catch (error) {
      console.error('Token refresh error:', error);
      logout();
      return null;
    }
  }, [logout]);

  // Initialize API client with token
  useEffect(() => {
    if (token) {
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Verify token and get user info
      getCurrentUser();
    } else {
      setLoading(false);
    }
  }, [getCurrentUser, token]);

  const hasRole = (role) => {
    if (!user) return false;
    if (role === 'admin') return user.role === 'admin';
    if (role === 'staff') return ['admin', 'staff'].includes(user.role);
    return true;
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    refreshToken,
    hasRole,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isStaff: ['admin', 'staff'].includes(user?.role),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

